import type { NoteDto, UpdateNoteInput } from '@keep/shared';
import { useIsMutating } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateNote } from '../../api/notes';
import { errorMessage } from '../../lib/api';
import { toastError } from '../../stores/toasts';
import { draftFromNote, draftPatch, type NoteDraft } from './draft';

const TEXT_DEBOUNCE_MS = 600;
const TEXT_FIELDS = new Set<keyof NoteDraft>(['title', 'content', 'items']);

/**
 * Brouillon d'une note existante, enregistré au fil de l'eau :
 * le texte après une courte pause, le reste (couleur, épingle…) immédiatement.
 * Les modifications distantes (autre onglet, collaborateur) sont reprises tant
 * qu'aucune saisie locale n'est en attente.
 */
export function useAutosave(note: NoteDto) {
  const { mutate } = useUpdateNote();
  const [draft, setDraft] = useState<NoteDraft>(() => draftFromNote(note));
  const pending = useRef<UpdateNoteInput>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saving = useIsMutating({
    mutationKey: ['note-update'],
    predicate: (m) => (m.state.variables as { id?: string } | undefined)?.id === note.id,
  });

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const patch = pending.current;
    if (Object.keys(patch).length === 0) return;
    pending.current = {};
    mutate({ id: note.id, patch }, { onError: (error) => toastError(errorMessage(error)) });
  }, [mutate, note.id]);

  const change = useCallback(
    (patch: Partial<NoteDraft>) => {
      setDraft((current) => ({ ...current, ...patch }));
      pending.current = { ...pending.current, ...draftPatch(patch) };
      const textOnly = Object.keys(patch).every((key) => TEXT_FIELDS.has(key as keyof NoteDraft));
      if (!textOnly) return flush();
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, TEXT_DEBOUNCE_MS);
    },
    [flush],
  );

  // Reprise des changements distants quand on n'a rien en attente.
  useEffect(() => {
    if (saving === 0 && Object.keys(pending.current).length === 0) setDraft(draftFromNote(note));
  }, [note, saving]);

  // Sauvegarde à la fermeture et avant de quitter la page.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    const beforeUnload = () => flushRef.current();
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      flushRef.current();
    };
  }, []);

  return { draft, change, flush, saving: saving > 0 };
}
